/** Presets SMTP por proveedor (Gmail, Microsoft, Yahoo, manual). */

export const EMAIL_TRANSPORT_PLATFORM = "platform";
export const EMAIL_TRANSPORT_SMTP = "smtp";
export const EMAIL_TRANSPORT_OAUTH_GOOGLE = "oauth_google";
export const EMAIL_TRANSPORT_OAUTH_MICROSOFT = "oauth_microsoft";

export const EMAIL_TRANSPORT_OPTIONS = [
  { id: EMAIL_TRANSPORT_PLATFORM, label: "Correo de la plataforma (recomendado)" },
  { id: EMAIL_TRANSPORT_OAUTH_GOOGLE, label: "Mi Gmail / Google" },
  { id: EMAIL_TRANSPORT_OAUTH_MICROSOFT, label: "Mi Outlook / Hotmail / Live" },
  { id: EMAIL_TRANSPORT_SMTP, label: "Otro (SMTP)" },
];

export const SMTP_PROVIDER_GMAIL = "gmail";
export const SMTP_PROVIDER_MICROSOFT = "microsoft";
export const SMTP_PROVIDER_YAHOO = "yahoo";
export const SMTP_PROVIDER_CUSTOM = "custom";

export const SMTP_PROVIDERS = [
  { id: SMTP_PROVIDER_GMAIL, label: "Gmail / Google Workspace" },
  { id: SMTP_PROVIDER_MICROSOFT, label: "Outlook / Hotmail / Live / Microsoft 365" },
  { id: SMTP_PROVIDER_YAHOO, label: "Yahoo Mail" },
  { id: SMTP_PROVIDER_CUSTOM, label: "Otro (configuración manual)" },
];

export const SMTP_PRESET_VALUES = {
  [SMTP_PROVIDER_GMAIL]: {
    smtp_host: "smtp.gmail.com",
    smtp_port: 587,
    smtp_use_tls: true,
    passwordHint:
      "Use una contraseña de aplicación de Google (no la contraseña normal de la cuenta).",
  },
  [SMTP_PROVIDER_MICROSOFT]: {
    smtp_host: "smtp-mail.outlook.com",
    smtp_port: 587,
    smtp_use_tls: true,
    passwordHint:
      "Outlook, Hotmail y Live requieren conexión con la cuenta de Microsoft. Use «Conectar Microsoft» en lugar de una contraseña.",
  },
  [SMTP_PROVIDER_YAHOO]: {
    smtp_host: "smtp.mail.yahoo.com",
    smtp_port: 587,
    smtp_use_tls: true,
    passwordHint: "Use una contraseña de aplicación generada en la cuenta Yahoo.",
  },
  [SMTP_PROVIDER_CUSTOM]: {
    smtp_host: "",
    smtp_port: 587,
    smtp_use_tls: true,
    passwordHint: "Indique host, puerto y credenciales que proporcione su proveedor de correo.",
  },
};

export function inferSmtpProvider(fromEmail) {
  const domain = String(fromEmail || "").trim().split("@")[1]?.toLowerCase();
  if (!domain) return SMTP_PROVIDER_CUSTOM;
  if (domain === "gmail.com" || domain === "googlemail.com") return SMTP_PROVIDER_GMAIL;
  if (
    ["outlook.com", "hotmail.com", "live.com", "msn.com"].includes(domain)
    || domain.startsWith("hotmail.")
    || domain.startsWith("outlook.")
    || domain.startsWith("live.")
  ) {
    return SMTP_PROVIDER_MICROSOFT;
  }
  if (domain.startsWith("yahoo.")) return SMTP_PROVIDER_YAHOO;
  return SMTP_PROVIDER_CUSTOM;
}

export function inferSmtpHost(fromEmail) {
  const provider = inferSmtpProvider(fromEmail);
  if (provider === SMTP_PROVIDER_CUSTOM) return "";
  return SMTP_PRESET_VALUES[provider]?.smtp_host || "";
}

export function isGmailEmail(fromEmail) {
  return inferSmtpProvider(fromEmail) === SMTP_PROVIDER_GMAIL;
}

export function isMicrosoftEmail(fromEmail) {
  return inferSmtpProvider(fromEmail) === SMTP_PROVIDER_MICROSOFT;
}

export function suggestEmailConnection(fromEmail) {
  const provider = inferSmtpProvider(fromEmail);
  if (provider === SMTP_PROVIDER_GMAIL) {
    return {
      provider,
      transport: EMAIL_TRANSPORT_OAUTH_GOOGLE,
      label: "Cuenta Google detectada",
      action: "Conectar Google",
    };
  }
  if (provider === SMTP_PROVIDER_MICROSOFT) {
    return {
      provider,
      transport: EMAIL_TRANSPORT_OAUTH_MICROSOFT,
      label: "Cuenta Microsoft detectada",
      action: "Conectar Microsoft",
    };
  }
  return null;
}

export const SIMPLE_EMAIL_ACCOUNT_GUIDANCE = [
  {
    id: "google",
    transport: EMAIL_TRANSPORT_OAUTH_GOOGLE,
    title: "Gmail o Google Workspace",
    detail: "Conectar con Google y aceptar. No escriba su contraseña normal.",
  },
  {
    id: "microsoft",
    transport: EMAIL_TRANSPORT_OAUTH_MICROSOFT,
    title: "Outlook, Hotmail, Live o Microsoft 365",
    detail: "Conectar con Microsoft y aceptar. Microsoft requiere acceso moderno.",
  },
  {
    id: "other",
    transport: EMAIL_TRANSPORT_SMTP,
    title: "Otro proveedor o dominio empresarial",
    detail: "Use SMTP avanzado con los datos y contraseña de aplicación de su proveedor.",
  },
];

/** @param {string} transportId */
export function guidanceTransportForId(transportId) {
  const row = SIMPLE_EMAIL_ACCOUNT_GUIDANCE.find((item) => item.transport === transportId);
  return row?.id || null;
}

export function platformSmtpReady(cfg) {
  const v = cfg?.platform_smtp_ready;
  return v === true || v === "true";
}

export function resolveEmailTransport(cfg) {
  const raw = String(cfg?.email_transport || "").trim();
  if (raw) return raw;
  if (cfg?.oauth_connected) {
    return cfg?.oauth_provider === "microsoft"
      ? EMAIL_TRANSPORT_OAUTH_MICROSOFT
      : EMAIL_TRANSPORT_OAUTH_GOOGLE;
  }
  if (!cfg) return EMAIL_TRANSPORT_PLATFORM;
  if (cfg.use_platform_smtp) return EMAIL_TRANSPORT_PLATFORM;
  if (cfg.smtp_workspace_configured || cfg.has_smtp_password) return EMAIL_TRANSPORT_SMTP;
  return EMAIL_TRANSPORT_PLATFORM;
}

export function usesOAuthEmail(cfg) {
  const transport = resolveEmailTransport(cfg);
  return transport === EMAIL_TRANSPORT_OAUTH_GOOGLE || transport === EMAIL_TRANSPORT_OAUTH_MICROSOFT;
}

/** Modo plataforma activo (por defecto para compradores sin SMTP propio). */
export function usesPlatformEmail(cfg) {
  return resolveEmailTransport(cfg) === EMAIL_TRANSPORT_PLATFORM;
}

export function isOAuthTransport(transport) {
  return transport === EMAIL_TRANSPORT_OAUTH_GOOGLE || transport === EMAIL_TRANSPORT_OAUTH_MICROSOFT;
}

export function emailTransportStatusLabel(cfg) {
  if (!cfg) return "Pendiente";
  if (cfg.transport_ready) {
    if (usesOAuthEmail(cfg)) {
      const who = cfg.oauth_provider === "microsoft" ? "Microsoft" : "Google";
      return `Listo (${who})`;
    }
    return "Listo para enviar";
  }
  if (usesOAuthEmail(cfg)) return "Conecte su cuenta";
  if (usesPlatformEmail(cfg)) return "Pendiente de activación";
  return "SMTP pendiente";
}

export function smtpPresetForProvider(providerId) {
  return SMTP_PRESET_VALUES[providerId] || SMTP_PRESET_VALUES[SMTP_PROVIDER_CUSTOM];
}

export function applySmtpProviderToForm(form, providerId, { fromEmail = "" } = {}) {
  const preset = smtpPresetForProvider(providerId);
  const previousFromEmail = String(form.from_email || "").trim().toLowerCase();
  const currentSmtpUser = String(form.smtp_user || "").trim();
  const shouldSyncUser =
    !currentSmtpUser
    || currentSmtpUser.toLowerCase() === previousFromEmail;
  return {
    ...form,
    smtp_provider: providerId,
    smtp_host: providerId === SMTP_PROVIDER_CUSTOM ? form.smtp_host : preset.smtp_host,
    smtp_port: preset.smtp_port,
    smtp_use_tls: preset.smtp_use_tls,
    smtp_user: shouldSyncUser
      ? (fromEmail || form.from_email || "")
      : form.smtp_user,
  };
}

export function syncSmtpProviderFromEmail(form, fromEmail) {
  const transport = resolveEmailTransport(form);
  const currentSmtpUser = String(form.smtp_user || "").trim().toLowerCase();
  const previousFromEmail = String(form.from_email || "").trim().toLowerCase();
  const syncSmtpUser = !currentSmtpUser || currentSmtpUser === previousFromEmail;
  const withEmail = {
    ...form,
    from_email: fromEmail,
    smtp_user: syncSmtpUser ? fromEmail : form.smtp_user,
  };
  if (isOAuthTransport(transport)) {
    return withEmail;
  }
  const detected = inferSmtpProvider(fromEmail);
  if (form.smtp_provider !== SMTP_PROVIDER_CUSTOM && form.smtp_provider !== detected) {
    return withEmail;
  }
  return applySmtpProviderToForm(withEmail, detected, { fromEmail });
}

/** Arma el payload de guardado sin mezclar SMTP cuando el modo es OAuth. */
export function buildEmailConfigSavePayload(formState) {
  const fromEmail = formState.from_email.trim();
  const transport = formState.email_transport || EMAIL_TRANSPORT_PLATFORM;
  const usePlatform = transport === EMAIL_TRANSPORT_PLATFORM;
  const oauthMode = isOAuthTransport(transport);
  const provider = oauthMode
    ? SMTP_PROVIDER_CUSTOM
    : (formState.smtp_provider || inferSmtpProvider(fromEmail));
  const preset = smtpPresetForProvider(provider);
  const smtpHost = oauthMode
    ? ""
    : (
      formState.smtp_host.trim()
      || (provider !== SMTP_PROVIDER_CUSTOM ? preset.smtp_host : "")
      || inferSmtpHost(fromEmail)
    );
  const payload = {
    enabled: formState.enabled,
    use_platform_smtp: usePlatform,
    email_transport: transport,
    from_email: fromEmail,
    from_name: formState.from_name.trim(),
    reply_to: formState.reply_to.trim(),
    subject_prefix: formState.subject_prefix.trim(),
    default_subject: formState.default_subject.trim(),
    default_body: formState.default_body,
    footer_text: formState.footer_text,
    append_footer: formState.append_footer,
    smtp_provider: provider,
    smtp_host: smtpHost,
    smtp_port: Number(formState.smtp_port) || 587,
    smtp_user: oauthMode ? fromEmail : (formState.smtp_user.trim() || fromEmail),
    smtp_use_tls: formState.smtp_use_tls,
    clear_smtp_password: Boolean(formState.clear_smtp_password) || oauthMode,
  };
  if (!oauthMode && formState.smtp_password.trim()) {
    payload.smtp_password = formState.smtp_password.trim();
  }
  return payload;
}

export function smtpPendingHint(cfg) {
  if (!cfg || cfg.transport_ready) return null;
  if (usesOAuthEmail(cfg)) {
    if (!cfg.oauth_connected) {
      const providers = cfg.oauth_providers || {};
      const transport = resolveEmailTransport(cfg);
      const googleMode = transport === EMAIL_TRANSPORT_OAUTH_GOOGLE;
      const msMode = transport === EMAIL_TRANSPORT_OAUTH_MICROSOFT;
      const googleOk = providers.google?.configured;
      const msOk = providers.microsoft?.configured;
      if (googleMode && !googleOk) {
        return cfg.platform_oauth_can_edit
          ? "Faltan las credenciales de Google (panel del operador)."
          : "Google aún no disponible. Use la plataforma.";
      }
      if (msMode && !msOk) {
        return cfg.platform_oauth_can_edit
          ? "Faltan las credenciales de Microsoft (panel del operador)."
          : "Microsoft aún no disponible. Use la plataforma.";
      }
      if (!googleOk && !msOk) return "La conexión con Google o Microsoft aún no está disponible. Use el correo de la plataforma.";
      return googleMode ? "Pulse Conectar Google." : msMode ? "Pulse Conectar Microsoft." : "Conecte su cuenta.";
    }
    return "Guarde para activar.";
  }
  if (usesPlatformEmail(cfg)) {
    if (!String(cfg.from_email || "").trim()) return "Indique el correo del despacho.";
    if (!platformSmtpReady(cfg)) {
      const providers = cfg.oauth_providers || {};
      if (providers.google?.configured) {
        return (
          "El envío por plataforma BNT aún no está activo en el servidor. "
          + "Abra «Usar mi Gmail…», elija Conectar Google, autorice y pulse Guardar."
        );
      }
      if (providers.microsoft?.configured) {
        return (
          "El envío por plataforma BNT aún no está activo en el servidor. "
          + "Abra «Usar mi Gmail…», elija Conectar Microsoft, autorice y pulse Guardar."
        );
      }
      return (
        "Activar envíos ya está listo; falta el SMTP de la plataforma en el servidor. "
        + "Use «Usar mi Gmail…» o contacte a soporte para activar BNT."
      );
    }
    return "Guarde para activar.";
  }
  const missing = [];
  const host = String(cfg.smtp_host || "").trim() || inferSmtpHost(cfg.from_email);
  if (!host) missing.push("servidor");
  if (!String(cfg.smtp_user || "").trim() && !String(cfg.from_email || "").trim()) {
    missing.push("usuario");
  }
  if (!cfg.has_smtp_password) {
    missing.push("contraseña de aplicación");
  } else if (cfg.smtp_password_effective === false) {
    return "Vuelva a escribir la contraseña y guarde.";
  } else if (host && (String(cfg.smtp_user || "").trim() || String(cfg.from_email || "").trim())) {
    return "Revise la contraseña y guarde.";
  }
  if (!missing.length) return "Complete SMTP y guarde.";
  return `Falta: ${missing.join(", ")}.`;
}
